import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Plus, Calendar, TrendingUp, Gift, Megaphone, ChefHat, Utensils, 
  PartyPopper, X, Upload, Video, Image as ImageIcon, Eye, Heart, MessageSquare,
  Trash2, Share2, CheckCircle2, XCircle, AlertCircle, Link2, Settings,
  MousePointerClick, ShoppingBag, DollarSign
} from "lucide-react";
import { SiFacebook, SiInstagram } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useUpload } from "@/hooks/use-upload";
import { Link } from "wouter";
import PartnerLayout from "./PartnerLayout";
import { SocialMediaPreview } from "@/components/partner/SocialMediaPreview";
import { MediaValidation } from "@/components/partner/MediaValidation";
import { SocialMediaAnalytics } from "@/components/partner/SocialMediaAnalytics";

interface PartnerPost {
  id: string;
  text: string;
  postType: string;
  promotionDetails: {
    type: string;
    title: string;
    couponCode?: string;
    eventDate?: string;
    expiresAt?: string;
  } | null;
  likes: number;
  comments: number;
  views?: number;
  createdAt: string;
  images?: string[];
  items?: Array<{
    name: string;
    description: string;
    image?: string;
  }>;
}

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  category: 'general' | 'item';
  itemName?: string;
  itemDescription?: string;
}

interface SocialConnection {
  id: string;
  platform: 'facebook' | 'instagram' | 'tiktok';
  platformAccountName: string | null;
  platformAccountHandle: string | null;
  isActive: boolean;
}

interface SocialPostResult {
  platform: string;
  success: boolean;
  error?: string;
}

const promotionTypes = [
  { value: 'chef_special', label: "Chef's Special", icon: ChefHat },
  { value: 'menu_update', label: 'Menu Update', icon: Utensils },
  { value: 'event', label: 'Event', icon: PartyPopper },
  { value: 'promotion', label: 'Promotion', icon: Gift },
  { value: 'announcement', label: 'Announcement', icon: Megaphone },
];

interface PostAttribution {
  shortLinkCode: string | null;
  shortLinkUrl: string | null;
  clicks: number;
  orders: number;
  revenue: string;
  platform: string | null;
}

function PostAttributionCard({ postId }: { postId: string }) {
  const { data, isLoading } = useQuery<PostAttribution>({
    queryKey: ['/api/partner/posts', postId, 'attribution'],
    queryFn: async () => {
      const res = await fetch(`/api/partner/posts/${postId}/attribution`, {
        headers: {
          ...(localStorage.getItem('auth_token') ? { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } : {}),
          ...(localStorage.getItem('partnerRestaurantId') ? { 'x-restaurant-id': localStorage.getItem('partnerRestaurantId')! } : {}),
          ...(sessionStorage.getItem('partnerEatzyToken') ? { 'x-eatzy-token': sessionStorage.getItem('partnerEatzyToken')! } : {}),
        },
      });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || !data || !data.shortLinkCode) return null;

  return (
    <div className="flex items-center gap-3 flex-wrap pt-3 border-t text-sm text-muted-foreground">
      <div className="flex items-center gap-1.5">
        <Share2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="font-mono text-xs text-foreground">{data.shortLinkCode}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <MousePointerClick className="h-3.5 w-3.5" />
        <span>{data.clicks} click{data.clicks !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <ShoppingBag className="h-3.5 w-3.5" />
        <span>{data.orders} order{data.orders !== 1 ? 's' : ''}</span>
      </div>
      <div className="flex items-center gap-1.5">
        <DollarSign className="h-3.5 w-3.5" />
        <span>${Number(data.revenue).toFixed(0)} revenue</span>
      </div>
    </div>
  );
}

export default function PartnerPosts() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem('partnerRestaurantId');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [postText, setPostText] = useState('');
  const [promotionType, setPromotionType] = useState('');
  const [promotionTitle, setPromotionTitle] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [isUploadingGeneral, setIsUploadingGeneral] = useState(false);
  const [isUploadingItem, setIsUploadingItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [crossPostPlatforms, setCrossPostPlatforms] = useState<string[]>([]);
  const [crossPostResults, setCrossPostResults] = useState<SocialPostResult[] | null>(null);
  const [scheduledFor, setScheduledFor] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  
  const { uploadFile } = useUpload({
    onSuccess: () => {},
    onError: (error) => {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const { data: posts, isLoading } = useQuery<PartnerPost[]>({
    queryKey: ['/api/partner/posts', restaurantId],
    enabled: !!restaurantId,
  });

  const { data: socialConnections } = useQuery<SocialConnection[]>({
    queryKey: ['/api/partner/social/connections', restaurantId],
    queryFn: async () => {
      const response = await fetch(`/api/partner/social/connections?restaurantId=${restaurantId}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!restaurantId,
  });

  const hasConnectedPlatforms = socialConnections && socialConnections.length > 0;

  const createPostMutation = useMutation({
    mutationFn: async (data: {
      text: string;
      promotionType: string;
      promotionTitle: string;
      couponCode?: string;
      eventDate?: string;
      crossPostPlatforms?: string[];
      expiresAt?: string;
      scheduledFor?: string;
      generalMedia: Array<{ url: string; type: string }>;
      items: Array<{ name: string; description: string; mediaUrl?: string; mediaType?: string }>;
    }) => {
      return apiRequest('POST', `/api/partner/posts`, {
        ...data,
        restaurantId,
      });
    },
    onSuccess: () => {
      toast({ title: "Post created successfully!" });
      queryClient.invalidateQueries({ queryKey: ['/api/partner/posts', restaurantId] });
      resetForm();
      setShowCreatePost(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create post",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setPostText('');
    setPromotionType('');
    setPromotionTitle('');
    setCouponCode('');
    setEventDate('');
    setExpiresAt('');
    setMediaItems([]);
    setNewItemName('');
    setNewItemDescription('');
    setCrossPostPlatforms([]);
    setCrossPostResults(null);
    setScheduledFor('');
    setIsScheduled(false);
  };

  
  const handleGeneralMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setIsUploadingGeneral(true);
    try {
      const response = await uploadFile(file);
      if (response) {
        const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
        setMediaItems(prev => [...prev, {
          url: response.objectPath,
          type: mediaType,
          category: 'general',
        }]);
        toast({ title: "Media uploaded successfully!" });
      }
    } finally {
      setIsUploadingGeneral(false);
      e.target.value = '';
    }
  };

  const handleItemMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!newItemName.trim()) {
      toast({
        title: "Item name required",
        description: "Please enter an item name before uploading media",
        variant: "destructive",
      });
      e.target.value = '';
      return;
    }
    
    setIsUploadingItem(true);
    try {
      const response = await uploadFile(file);
      if (response) {
        const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
        setMediaItems(prev => [...prev, {
          url: response.objectPath,
          type: mediaType,
          category: 'item',
          itemName: newItemName.trim(),
          itemDescription: newItemDescription.trim(),
        }]);
        setNewItemName('');
        setNewItemDescription('');
        toast({ title: "Item media uploaded successfully!" });
      }
    } finally {
      setIsUploadingItem(false);
      e.target.value = '';
    }
  };

  const removeMedia = (index: number) => {
    setMediaItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!postText.trim()) {
      toast({
        title: "Post text required",
        description: "Please enter some text for your post",
        variant: "destructive",
      });
      return;
    }

    if (!promotionType) {
      toast({
        title: "Post type required",
        description: "Please select a post type",
        variant: "destructive",
      });
      return;
    }

    const generalMedia = mediaItems
      .filter(m => m.category === 'general')
      .map(m => ({ url: m.url, type: m.type }));
    
    const items = mediaItems
      .filter(m => m.category === 'item')
      .map(m => ({
        name: m.itemName || '',
        description: m.itemDescription || '',
        mediaUrl: m.url,
        mediaType: m.type,
      }));

    createPostMutation.mutate({
      text: postText,
      promotionType,
      promotionTitle: promotionTitle || promotionTypes.find(p => p.value === promotionType)?.label || '',
      couponCode: couponCode || undefined,
      eventDate: eventDate || undefined,
      expiresAt: expiresAt || undefined,
      scheduledFor: isScheduled && scheduledFor ? scheduledFor : undefined,
      generalMedia,
      items,
      crossPostPlatforms: crossPostPlatforms.length > 0 ? crossPostPlatforms : undefined,
    });
  };

  const getPromotionIcon = (type: string) => {
    const promo = promotionTypes.find(p => p.value === type);
    return promo?.icon || Megaphone;
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-posts-title">Content Posts</h1>
            <p className="text-muted-foreground">Create posts that appear in the For You feed</p>
          </div>
          
          <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
            <DialogTrigger asChild>
              <Button data-testid="button-create-post">
                <Plus className="h-4 w-4 mr-1" />
                Create Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90svh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Content Post</DialogTitle>
                <DialogDescription>
                  Create a promotional post that will appear in the For You feed
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="post-type">Post Type *</Label>
                  <Select value={promotionType} onValueChange={setPromotionType}>
                    <SelectTrigger data-testid="select-post-type">
                      <SelectValue placeholder="Select post type" />
                    </SelectTrigger>
                    <SelectContent>
                      {promotionTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="post-title">Title (optional)</Label>
                  <Input 
                    id="post-title"
                    placeholder="e.g., Valentine's Day Special"
                    value={promotionTitle}
                    onChange={(e) => setPromotionTitle(e.target.value)}
                    data-testid="input-post-title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="post-text">Post Content *</Label>
                  <Textarea 
                    id="post-text"
                    placeholder="Describe your special offer, menu item, or announcement..."
                    value={postText}
                    onChange={(e) => setPostText(e.target.value)}
                    className="min-h-[100px]"
                    data-testid="input-post-text"
                  />
                </div>

                {(promotionType === 'promotion' || promotionType === 'event') && (
                  <div className="grid grid-cols-2 gap-4">
                    {promotionType === 'promotion' && (
                      <div className="space-y-2">
                        <Label htmlFor="coupon-code">Coupon Code</Label>
                        <Input 
                          id="coupon-code"
                          placeholder="e.g., LOVE25"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value)}
                          data-testid="input-coupon-code"
                        />
                      </div>
                    )}
                    {promotionType === 'event' && (
                      <div className="space-y-2">
                        <Label htmlFor="event-date">Event Date</Label>
                        <Input 
                          id="event-date"
                          type="datetime-local"
                          value={eventDate}
                          onChange={(e) => setEventDate(e.target.value)}
                          data-testid="input-event-date"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="expires-at">Expires At</Label>
                      <Input 
                        id="expires-at"
                        type="datetime-local"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                        data-testid="input-expires-at"
                      />
                    </div>
                  </div>
                )}

                <Tabs defaultValue="general" className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="general">General Media</TabsTrigger>
                    <TabsTrigger value="items">Menu Items</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="general" className="space-y-4">
                    <div className="border-2 border-dashed rounded-lg p-4 text-center">
                      <input
                        type="file"
                        accept="image/*,video/*"
                        onChange={handleGeneralMediaUpload}
                        className="hidden"
                        id="general-media-upload"
                        disabled={isUploadingGeneral}
                      />
                      <label htmlFor="general-media-upload" className="cursor-pointer">
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="h-8 w-8 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {isUploadingGeneral ? 'Uploading...' : 'Click to upload promo image or video'}
                          </span>
                        </div>
                      </label>
                    </div>
                    
                    {mediaItems.filter(m => m.category === 'general').map((media, index) => (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center gap-2 p-2 border rounded-lg">
                          {media.type === 'video' ? (
                            <Video className="h-5 w-5 text-blue-500" />
                          ) : (
                            <ImageIcon className="h-5 w-5 text-green-500" />
                          )}
                          <span className="flex-1 text-sm truncate">{media.url}</span>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => removeMedia(mediaItems.indexOf(media))}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        {media.type === 'image' && crossPostPlatforms.length > 0 && (
                          <MediaValidation
                            url={media.url}
                            platforms={crossPostPlatforms}
                          />
                        )}
                      </div>
                    ))}
                  </TabsContent>
                  
                  <TabsContent value="items" className="space-y-4">
                    <div className="space-y-4 p-4 border rounded-lg">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="item-name">Item Name *</Label>
                          <Input 
                            id="item-name"
                            placeholder="e.g., Flame Grilled Tenderloin"
                            value={newItemName}
                            onChange={(e) => setNewItemName(e.target.value)}
                            data-testid="input-item-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="item-description">Description</Label>
                          <Input 
                            id="item-description"
                            placeholder="e.g., Grilled to perfection..."
                            value={newItemDescription}
                            onChange={(e) => setNewItemDescription(e.target.value)}
                            data-testid="input-item-description"
                          />
                        </div>
                      </div>
                      
                      <div className="border-2 border-dashed rounded-lg p-4 text-center">
                        <input
                          type="file"
                          accept="image/*,video/*"
                          onChange={handleItemMediaUpload}
                          className="hidden"
                          id="item-media-upload"
                          disabled={isUploadingItem || !newItemName.trim()}
                        />
                        <label 
                          htmlFor="item-media-upload" 
                          className={`cursor-pointer ${!newItemName.trim() ? 'opacity-50' : ''}`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <Utensils className="h-8 w-8 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {isUploadingItem ? 'Uploading...' : 'Upload item photo or video'}
                            </span>
                            {!newItemName.trim() && (
                              <span className="text-xs text-amber-600">Enter item name first</span>
                            )}
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {mediaItems.filter(m => m.category === 'item').map((media, index) => (
                      <div key={index} className="flex items-center gap-2 p-2 border rounded-lg">
                        {media.type === 'video' ? (
                          <Video className="h-5 w-5 text-blue-500" />
                        ) : (
                          <ImageIcon className="h-5 w-5 text-green-500" />
                        )}
                        <div className="flex-1">
                          <div className="text-sm font-medium">{media.itemName}</div>
                          {media.itemDescription && (
                            <div className="text-xs text-muted-foreground">{media.itemDescription}</div>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeMedia(mediaItems.indexOf(media))}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </TabsContent>
                </Tabs>
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4" />
                  <Label className="text-sm font-medium">Schedule Post</Label>
                </div>
                
                <label 
                  htmlFor="schedule-toggle"
                  className="flex items-center gap-3 p-3 border rounded-lg hover-elevate cursor-pointer"
                >
                  <Checkbox
                    id="schedule-toggle"
                    checked={isScheduled}
                    onCheckedChange={(checked) => {
                      setIsScheduled(!!checked);
                      if (!checked) setScheduledFor('');
                    }}
                  />
                  <span className="text-sm">Schedule for later</span>
                </label>
                
                {isScheduled && (
                  <div className="mt-3">
                    <Label htmlFor="scheduled-datetime" className="text-xs text-muted-foreground">
                      Schedule Date & Time
                    </Label>
                    <Input
                      id="scheduled-datetime"
                      type="datetime-local"
                      value={scheduledFor}
                      onChange={(e) => setScheduledFor(e.target.value)}
                      min={new Date().toISOString().slice(0, 16)}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>

              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Share2 className="h-4 w-4" />
                  <Label className="text-sm font-medium">Cross-post to social media</Label>
                </div>
                
                {hasConnectedPlatforms ? (
                  <div className="space-y-2">
                    {socialConnections?.map((connection) => (
                      <label 
                        key={connection.id} 
                        htmlFor={`crosspost-${connection.platform}`}
                        className="flex items-center gap-3 p-3 border rounded-lg hover-elevate cursor-pointer"
                      >
                        <Checkbox
                          id={`crosspost-${connection.platform}`}
                          checked={crossPostPlatforms.includes(connection.platform)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setCrossPostPlatforms(prev => [...prev, connection.platform]);
                            } else {
                              setCrossPostPlatforms(prev => prev.filter(p => p !== connection.platform));
                            }
                          }}
                        />
                        <div className="flex items-center gap-2">
                          {connection.platform === 'facebook' && (
                            <SiFacebook className="h-5 w-5 text-[#1877F2]" />
                          )}
                          {connection.platform === 'instagram' && (
                            <SiInstagram className="h-5 w-5 text-[#E4405F]" />
                          )}
                          <div>
                            <span className="capitalize font-medium">{connection.platform}</span>
                            {connection.platformAccountName && (
                              <span className="text-sm text-muted-foreground ml-2">
                                {connection.platformAccountHandle 
                                  ? `@${connection.platformAccountHandle}` 
                                  : connection.platformAccountName}
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    ))}
                    
                    {crossPostPlatforms.length > 0 && (
                      <>
                        <Alert className="mt-2 bg-muted/50">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            Your post will be shared to {crossPostPlatforms.length} platform{crossPostPlatforms.length > 1 ? 's' : ''} after creation.
                            {crossPostPlatforms.includes('instagram') && mediaItems.length === 0 && (
                              <span className="block text-amber-600 mt-1">
                                Note: Instagram posts require at least one image.
                              </span>
                            )}
                          </AlertDescription>
                        </Alert>
                        
                        {postText.trim() && (
                          <SocialMediaPreview
                            content={{
                              text: postText,
                              title: promotionTitle || undefined,
                              type: promotionType || undefined,
                            }}
                            platforms={crossPostPlatforms}
                            imageUrl={mediaItems.find(m => m.type === 'image')?.url}
                            hashtagsInFirstComment={true}
                          />
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
                    <Link2 className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">
                        No social media accounts connected
                      </p>
                    </div>
                    <Link href="/partner/settings">
                      <Button variant="outline" size="sm" className="gap-1">
                        <Settings className="h-3 w-3" />
                        Connect
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
              
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setShowCreatePost(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={createPostMutation.isPending || !postText.trim() || !promotionType}
                  data-testid="button-submit-post"
                >
                  {createPostMutation.isPending ? 'Creating...' : 'Create Post'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {restaurantId && socialConnections && socialConnections.length > 0 && (
          <SocialMediaAnalytics restaurantId={restaurantId} />
        )}

        {isLoading ? (
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-1/4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : posts && posts.length > 0 ? (
          <div className="grid gap-4">
            {posts.map((post) => {
              const PromoIcon = getPromotionIcon(post.promotionDetails?.type || '');
              return (
                <Card key={post.id} data-testid={`post-card-${post.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="gap-1">
                          <PromoIcon className="h-3 w-3" />
                          {post.promotionDetails?.title || post.promotionDetails?.type || 'Post'}
                        </Badge>
                        {post.promotionDetails?.couponCode && (
                          <Badge variant="outline">{post.promotionDetails.couponCode}</Badge>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {formatDate(post.createdAt)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm mb-4">{post.text}</p>
                    
                    {post.items && post.items.length > 0 && (
                      <div className="flex gap-2 flex-wrap mb-4">
                        {post.items.map((item, idx) => (
                          <Badge key={idx} variant="outline" className="gap-1">
                            <Utensils className="h-3 w-3" />
                            {item.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Heart className="h-4 w-4" />
                        {post.likes}
                      </div>
                      <div className="flex items-center gap-1">
                        <MessageSquare className="h-4 w-4" />
                        {post.comments}
                      </div>
                      {post.views !== undefined && (
                        <div className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          {post.views}
                        </div>
                      )}
                    </div>
                    <PostAttributionCard postId={post.id} />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Megaphone className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No posts yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Create your first post to engage customers in the For You feed
              </p>
              <Button onClick={() => setShowCreatePost(true)} data-testid="button-create-first-post">
                <Plus className="h-4 w-4 mr-1" />
                Create Your First Post
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </PartnerLayout>
  );
}
